import { isAndroidWeb } from "@/src/features/play-banner/play-banner.utils";
import ScButton from "@/src/components/ui-kit/button";
import { usePwaInstall } from "@/src/hooks/features/pwa/use-pwa-install";
import { CloseCircle } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Image, Pressable, Text, View } from "react-native";

/**
 * The one-time offer at the top of the feed.
 *
 * Same three realities as the settings card, but it has to stay quiet: it is a
 * nudge, not a gate. It renders nothing once the app is installed, once dismissed
 * (remembered per device), or when the browser cannot install. On iOS there is no
 * prompt to fire, so it shows the Share → Add to Home Screen line instead of a
 * button that would do nothing.
 */
const InstallAppBanner = () => {
  const { t } = useTranslation();
  const {
    canPrompt,
    showIosSteps,
    iosNeedsSafari,
    isInstalled,
    isBannerDismissed,
    dismissBanner,
    promptInstall,
  } = usePwaInstall();

  // Android visitors get the Google Play prompt (PlayStoreBanner) instead.
  if (isAndroidWeb()) return null;
  if (isInstalled || isBannerDismissed) return null;
  if (!canPrompt && !showIosSteps && !iosNeedsSafari) return null;

  const hint = showIosSteps
    ? t("HINT_INSTALL_IOS")
    : iosNeedsSafari
      ? t("HINT_INSTALL_IOS_SAFARI")
      : t("HINT_INSTALL_BANNER");

  return (
    <View className="flex-row items-center gap-3 rounded-2xl border border-ash-3 bg-chalk p-3">
      {/*
        Sized with `style`, not a `w-10 h-10` class: on web the class did not reach
        the <img>, which rendered at its natural 128px and squeezed the text into a
        column that broke mid-word ("Install / SELIS / E Club") on a 390px screen.
      */}
      <Image
        source={require("@/src/assets/images/club_logo.png")}
        style={{ width: 40, height: 40, borderRadius: 12 }}
        alt=""
      />

      {/*
        `minWidth: 0` is what lets this column shrink in a flex row on web — without
        it the text takes its max-content width and pushes everything else out.
      */}
      <View style={{ flex: 1, minWidth: 0 }} className="gap-0.5">
        <Text numberOfLines={1} className="font-medium text-sm text-onyx">
          {t("LABEL_INSTALL_BANNER")}
        </Text>
        <Text numberOfLines={2} className="text-xs text-slate">
          {hint}
        </Text>
      </View>

      {canPrompt && (
        <ScButton
          text="BTN_INSTALL_APP"
          onPress={() => {
            void promptInstall();
          }}
        />
      )}

      <Pressable
        onPress={dismissBanner}
        accessibilityLabel={t("BTN_NOT_NOW")}
        accessibilityRole="button"
        className="p-1 active:opacity-70"
      >
        <CloseCircle size={20} className="text-slate" />
      </Pressable>
    </View>
  );
};

export default InstallAppBanner;
